import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  RefreshCw, 
  CheckCircle2, 
  XCircle, 
  Clock, 
  Globe, 
  ExternalLink,
  Cloud,
  Image
} from 'lucide-react';
import { format } from 'date-fns';


interface IndexingStatusPanelProps {
  siteId: string;
  canonicalBaseUrl: string | null;
  primaryDomain: string | null;
  embedded?: boolean;
}

interface IndexingStatus {
  sitemap_exists: boolean;
  sitemap_last_generated: string | null;
  sitemap_url: string | null;
  robots_exists: boolean;
  robots_last_updated: string | null;
  google_sitemap_submitted: boolean;
  google_last_submission: string | null;
  bing_sitemap_submitted: boolean;
  bing_last_submission: string | null;
  indexnow_enabled: boolean;
  indexnow_last_notification: string | null;
  indexnow_urls_submitted: number;
  last_global_reindex: string | null;
}

interface IndexingLog {
  id: string;
  action_type: string;
  target_system: string | null;
  status: string;
  error_message: string | null;
  created_at: string;
  urls_affected: string[] | null;
}

export function IndexingStatusPanel({ siteId, canonicalBaseUrl, primaryDomain, embedded = false }: IndexingStatusPanelProps) {
  const [status, setStatus] = useState<IndexingStatus | null>(null);
  const [logs, setLogs] = useState<IndexingLog[]>([]);
  const [isReindexing, setIsReindexing] = useState(false);
  const [isRegisteringBing, setIsRegisteringBing] = useState(false);
  const [isConfiguringCloudflare, setIsConfiguringCloudflare] = useState(false);
  const [isRedeployingWorker, setIsRedeployingWorker] = useState(false);
  const [isConvertingImages, setIsConvertingImages] = useState(false);
  const [cloudflareStatus, setCloudflareStatus] = useState<'unknown' | 'configured' | 'needs_setup'>('unknown');

  const baseUrl = canonicalBaseUrl || (primaryDomain ? `https://${primaryDomain}` : '');

  useEffect(() => {
    fetchStatus();
    fetchLogs();
  }, [siteId]);

  const fetchStatus = async () => {
    const { data } = await supabase
      .from('site_indexing_status')
      .select('*')
      .eq('site_id', siteId)
      .single();
    
    if (data) {
      setStatus(data as unknown as IndexingStatus);
    }
  };

  const fetchLogs = async () => {
    const { data } = await supabase
      .from('indexing_logs')
      .select('id, action_type, target_system, status, error_message, created_at, urls_affected')
      .eq('site_id', siteId)
      .order('created_at', { ascending: false })
      .limit(20);
    
    if (data) {
      setLogs(data as IndexingLog[]);
    }
  };

  const triggerReindex = async () => {
    setIsReindexing(true);
    try {
      const { data, error } = await supabase.functions.invoke('trigger-global-reindex', {
        body: { siteId }
      });

      if (error) throw error;
      
      toast.success('Global re-index completed', {
        description: `Sitemap: ${data.results.sitemap.success ? '✓' : '✗'}, IndexNow: ${data.results.indexnow.success ? `✓ (${data.results.indexnow.urlCount} URLs)` : '✗'}`
      });
      
      fetchStatus();
      fetchLogs();
    } catch (error) {
      toast.error('Re-index failed', { description: String(error) });
    } finally {
      setIsReindexing(false);
    }
  };

  const registerWithBing = async () => {
    if (!primaryDomain) {
      toast.error('No domain configured', { 
        description: 'Please set a primary domain in Site Settings first' 
      });
      return;
    }

    setIsRegisteringBing(true);
    try {
      const { data, error } = await supabase.functions.invoke('register-bing-site', {
        body: { siteId }
      });

      if (error) throw error;
      
      if (data.success) {
        toast.success(data.alreadyExists ? 'Site already registered' : 'Site registered with Bing', {
          description: data.message,
          duration: 8000
        });
        
        // Show next steps
        toast.info('Next steps', {
          description: 'Go to Bing Webmaster Tools to complete verification',
          duration: 10000
        });
      } else {
        toast.error('Registration failed', { description: data.message });
      }
      
      fetchLogs();
    } catch (error) {
      toast.error('Failed to register with Bing', { description: String(error) });
    } finally {
      setIsRegisteringBing(false);
    }
  };

  const configureCloudflare = async () => {
    if (!primaryDomain) {
      toast.error('No domain configured', { 
        description: 'Please set a primary domain in Site Settings first' 
      });
      return;
    }

    setIsConfiguringCloudflare(true);
    try {
      const { data, error } = await supabase.functions.invoke('configure-cloudflare-worker', {
        body: { action: 'auto_configure', domain: primaryDomain }
      });

      if (error) throw error;

      if (data.setup_required) {
        setCloudflareStatus('needs_setup');
        toast.error('Cloudflare credentials required', {
          description: 'Add CLOUDFLARE_API_TOKEN and CLOUDFLARE_ACCOUNT_ID secrets',
          duration: 10000
        });
        return;
      }

      if (!data.success) {
        const failures = Array.isArray(data.failures) ? data.failures : [];
        const failureMsg = failures.length
          ? `${failures.length} route(s) failed to attach. Your token likely needs Workers Routes permissions.`
          : (data.error || data.message || 'Unknown error');

        setCloudflareStatus('needs_setup');
        toast.error('Cloudflare configuration failed', {
          description: failureMsg,
          duration: 12000
        });
        return;
      }

      setCloudflareStatus('configured');
      
      // Show more informative message based on what happened
      const actionMsg = data.workerDeployed 
        ? 'Global worker deployed + routes added' 
        : 'Routes added to existing global worker';
      
      toast.success('Cloudflare configured!', {
        description: `${actionMsg} for ${primaryDomain}`,
        duration: 8000
      });

      if (data.warning) {
        toast.info('One more step', {
          description: String(data.warning),
          duration: 12000
        });
      }

      fetchLogs();
    } catch (error) {
      setCloudflareStatus('needs_setup');
      toast.error('Failed to configure Cloudflare', { description: String(error) });
    } finally {
      setIsConfiguringCloudflare(false);
    }
  };

  const redeployWorker = async () => {
    setIsRedeployingWorker(true);
    try {
      const { data, error } = await supabase.functions.invoke('configure-cloudflare-worker', {
        body: { action: 'redeploy_worker' }
      });

      if (error) throw error;

      if (data.setup_required) {
        setCloudflareStatus('needs_setup');
        toast.error('Cloudflare credentials required', {
          description: 'Add CLOUDFLARE_API_TOKEN and CLOUDFLARE_ACCOUNT_ID secrets',
          duration: 10000
        });
        return;
      }

      if (!data.success) {
        toast.error('Worker deployment failed', {
          description: data.error || data.message || 'Unknown error',
          duration: 12000
        });
        return;
      }

      toast.success('Worker updated for all domains!', {
        description: 'The latest SEO proxy code is now live globally',
        duration: 8000
      });

      fetchLogs();
    } catch (error) {
      toast.error('Failed to redeploy worker', { description: String(error) });
    } finally {
      setIsRedeployingWorker(false);
    }
  };

  const convertAdsToJpeg = async () => {
    setIsConvertingImages(true);
    try {
      const { data, error } = await supabase.functions.invoke('convert-ads-to-jpeg', {
        body: {}
      });

      if (error) throw error;

      if (!data.success) {
        toast.error('Conversion failed', { description: data.error });
        return;
      }

      toast.success('Images converted!', {
        description: data.message,
        duration: 8000
      });
    } catch (error) {
      toast.error('Failed to convert images', { description: String(error) });
    } finally {
      setIsConvertingImages(false);
    }
  };

  const indexNowConfigured = true;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
  };

  const getActionLabel = (action: string) => {
    const labels: Record<string, string> = {
      'sitemap_generated': 'Sitemap Generated',
      'robots_generated': 'robots.txt Updated',
      'google_sitemap_submitted': 'Google Submission',
      'bing_sitemap_submitted': 'Bing Sitemap',
      'bing_site_registered': 'Bing Site Added',
      'bing_url_batch_submitted': 'Bing URLs Submitted',
      'indexnow_notification': 'IndexNow Notification',
      'auto_indexnow_site_enabled': 'Auto-IndexNow (Site Enabled)',
      'auto_indexnow_town_published': 'Auto-IndexNow (Town Published)',
      'auto_indexnow_area_published': 'Auto-IndexNow (Area Published)',
      'global_reindex': 'Global Re-index'
    };
    return labels[action] || action;
  };

  const content = (
    <div className="space-y-6">
        {/* Status Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Sitemap</span>
              {status?.sitemap_exists ? (
                <Badge variant="default" className="bg-green-600">Active</Badge>
              ) : (
                <Badge variant="secondary">Pending</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Last generated: {formatDate(status?.sitemap_last_generated || null)}
            </p>
            {baseUrl && (
              <a 
                href={`${baseUrl}/sitemap.xml`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                View Sitemap <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>

          <div className="p-4 border rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">robots.txt</span>
              {status?.robots_exists ? (
                <Badge variant="default" className="bg-green-600">Active</Badge>
              ) : (
                <Badge variant="secondary">Pending</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Last updated: {formatDate(status?.robots_last_updated || null)}
            </p>
            {baseUrl && (
              <a 
                href={`${baseUrl}/robots.txt`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                View robots.txt <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>

          <div className="p-4 border rounded-lg space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">IndexNow</span>
              {status?.indexnow_enabled ? (
                <Badge variant="default" className="bg-green-600">Enabled</Badge>
              ) : (
                <Badge variant="secondary">Disabled</Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              Last notification: {formatDate(status?.indexnow_last_notification || null)}
            </p>
            {status?.indexnow_urls_submitted ? (
              <p className="text-xs text-muted-foreground">
                {status.indexnow_urls_submitted} URLs submitted
              </p>
            ) : null}
          </div>
        </div>

        <Separator />

        {/* Cloudflare SEO Proxy Configuration */}
        <div className="p-4 border rounded-lg space-y-3 border-orange-200 bg-orange-50 dark:bg-orange-950 dark:border-orange-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium flex items-center gap-2">
                <Cloud className="h-4 w-4 text-orange-600" />
                Global SEO Proxy (Cloudflare)
              </p>
              <p className="text-xs text-muted-foreground">
                One worker serves all domains — adding this domain to the shared proxy
              </p>
            </div>
            <div className="flex gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={configureCloudflare}
                disabled={isConfiguringCloudflare || !primaryDomain}
                className="border-orange-300 hover:bg-orange-100"
              >
                {isConfiguringCloudflare ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Configuring...
                  </>
                ) : (
                  <>
                    <Cloud className="h-4 w-4 mr-2" />
                    Add Domain to Proxy
                  </>
                )}
              </Button>
              <Button 
                variant="default" 
                size="sm"
                onClick={redeployWorker}
                disabled={isRedeployingWorker}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {isRedeployingWorker ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Deploying...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Update Worker (All Domains)
                  </>
                )}
              </Button>
            </div>
          </div>
          {cloudflareStatus === 'needs_setup' && (
            <div className="text-xs text-orange-800 dark:text-orange-200 p-2 bg-orange-100 dark:bg-orange-900 rounded">
              <p className="font-medium mb-1">Setup Required:</p>
              <ol className="list-decimal ml-4 space-y-1">
                <li>Go to Cloudflare Dashboard → My Profile → API Tokens</li>
                <li>Create a token with <strong>Workers Scripts:Edit</strong> and <strong>Zone:Edit</strong> permissions</li>
                <li>Add <code>CLOUDFLARE_API_TOKEN</code> and <code>CLOUDFLARE_ACCOUNT_ID</code> as secrets</li>
              </ol>
            </div>
          )}
          {cloudflareStatus === 'configured' && (
            <div className="flex items-center gap-2 text-xs text-green-700 dark:text-green-400">
              <CheckCircle2 className="h-4 w-4" />
              Routes configured for {primaryDomain} → global SEO worker
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            <strong>How it works:</strong> A single Cloudflare Worker handles all your domains. 
            It injects page-specific SEO metadata (titles, descriptions, H1s) for towns, areas, and blog posts. 
            When you update the worker code, all domains get the update instantly. Metadata is cached 5 min at edge.
          </p>
        </div>

        <Separator />

        {/* Bing Registration */}
        <div className="p-4 border rounded-lg space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Bing Webmaster Tools</p>
              <p className="text-xs text-muted-foreground">
                Register your domain with Bing for faster crawling
              </p>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={registerWithBing}
              disabled={isRegisteringBing || !primaryDomain}
            >
              {isRegisteringBing ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Registering...
                </>
              ) : (
                <>
                  <Globe className="h-4 w-4 mr-2" />
                  Register with Bing
                </>
              )}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            After registering, complete verification at{' '}
            <a 
              href="https://www.bing.com/webmasters" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-primary underline"
            >
              Bing Webmaster Tools
            </a>
          </p>
        </div>

        {/* IndexNow Info */}
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-sm font-medium mb-1">IndexNow Integration</p>
          <p className="text-xs text-muted-foreground">
            IndexNow is configured globally and instantly notifies search engines (Bing, Yandex, Seznam) when content changes.
            URLs are automatically submitted when sites are enabled or content is published.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <Button onClick={triggerReindex} disabled={isReindexing}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isReindexing ? 'animate-spin' : ''}`} />
            {isReindexing ? 'Re-indexing...' : 'Trigger Global Re-index'}
          </Button>
          
          <Button 
            variant="outline" 
            onClick={convertAdsToJpeg} 
            disabled={isConvertingImages}
          >
            <Image className={`h-4 w-4 mr-2 ${isConvertingImages ? 'animate-pulse' : ''}`} />
            {isConvertingImages ? 'Converting...' : 'Convert PNG Ads to JPEG'}
          </Button>
        </div>

        {status?.last_global_reindex && (
          <p className="text-sm text-muted-foreground">
            Last global re-index: {formatDate(status.last_global_reindex)}
          </p>
        )}

        <Separator />

        {/* Activity Log */}
        <div>
          <h4 className="font-medium mb-3">Recent Activity</h4>
          {logs.length === 0 ? (
            <p className="text-sm text-muted-foreground">No indexing activity yet</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {logs.map((log) => (
                <div 
                  key={log.id} 
                  className="flex items-center justify-between p-2 border rounded text-sm"
                >
                  <div className="flex items-center gap-2">
                    {log.status === 'success' ? (
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    ) : log.status === 'failed' ? (
                      <XCircle className="h-4 w-4 text-red-600" />
                    ) : (
                      <Clock className="h-4 w-4 text-yellow-600" />
                    )}
                    <span>{getActionLabel(log.action_type)}</span>
                    {log.target_system && (
                      <Badge variant="outline" className="text-xs">
                        {log.target_system}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {log.error_message && (
                      <span className="text-xs text-red-600">{log.error_message}</span>
                    )}
                    <span className="text-xs text-muted-foreground">
                      {formatDate(log.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Setup Instructions */}
        <div className="p-4 bg-blue-50 dark:bg-blue-950 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="flex items-start gap-3">
            <div className="h-5 w-5 text-blue-600 mt-0.5 flex items-center justify-center">ℹ️</div>
            <div className="space-y-2">
              <h4 className="font-medium text-blue-900 dark:text-blue-100">Search Console Setup</h4>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                To submit your sitemap to Google and Bing:
              </p>
              <ol className="text-sm text-blue-800 dark:text-blue-200 list-decimal ml-4 space-y-1">
                <li>
                  <a 
                    href="https://search.google.com/search-console" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Google Search Console
                  </a>: Add your domain, verify ownership via DNS TXT record, then submit <code>{baseUrl}/sitemap.xml</code>
                </li>
                <li>
                  <a 
                    href="https://www.bing.com/webmasters" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="underline"
                  >
                    Bing Webmaster Tools
                  </a>: Import from Google Search Console or add manually, then submit sitemap
                </li>
              </ol>
            </div>
          </div>
      </div>
    </div>
  );

  if (embedded) {
    return content;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Globe className="h-5 w-5" />
          Search Engine Indexing
        </CardTitle>
        <CardDescription>
          Monitor and control how search engines discover your site
        </CardDescription>
      </CardHeader>
      <CardContent>
        {content}
      </CardContent>
    </Card>
  );
}